import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import { postPromoCodeValidation, registerWalletPaymentMethod } from '../../../utilities/api';
import { addressFields, FORM_REQUIREMENTS, SUPPORTED_PAYMENT_METHOD_TYPES } from '../../../utilities/constants';
import { buildSanitizedAddress, deepTrim } from '../../../utilities/form-sanitizer';
import { STAMPS_CREDENTIALS_FIELDS } from '../components/stamps-account-credentials/constants';
import { AddressTypeLookup, useSaveFirstTimeBillingInfo } from '../../../../hooks/use-first-time-billing-info';
import Global from '../../../../store/global-selectors';

const buildSanitizedStampsCredentials = (paymentMethodType, stampsAccountCredentials) => {
  if (!stampsAccountCredentials || paymentMethodType !== SUPPORTED_PAYMENT_METHOD_TYPES.STAMPS_CREDENTIALS) {
    return null;
  }

  return stampsAccountCredentials;
};

const buildSanitizedCreditCardInfo = (paymentMethodType, creditCardInfo) => {
  if (!creditCardInfo || paymentMethodType !== SUPPORTED_PAYMENT_METHOD_TYPES.CREDIT_CARD) {
    return null;
  }

  return creditCardInfo;
};

const buildSanitizedPaymentMethod = (paymentMethod) => {
  if (!paymentMethod) {
    return null;
  }

  return {
    ...paymentMethod,
    [FORM_REQUIREMENTS.CREDIT_CARD]: buildSanitizedCreditCardInfo(
      paymentMethod[FORM_REQUIREMENTS.PAYMENT_METHOD_TYPE],
      paymentMethod[FORM_REQUIREMENTS.CREDIT_CARD],
    ),
    [FORM_REQUIREMENTS.STAMPS_CREDENTIALS]: buildSanitizedStampsCredentials(
      paymentMethod[FORM_REQUIREMENTS.PAYMENT_METHOD_TYPE],
      paymentMethod[FORM_REQUIREMENTS.STAMPS_CREDENTIALS],
    ),
  };
};

export const clearNonNumericalCharsFromPhone = (walletForm) => {
  if (walletForm[FORM_REQUIREMENTS.BILLING_ADDRESS][addressFields.phone]) {
    return (walletForm[FORM_REQUIREMENTS.BILLING_ADDRESS][addressFields.phone].match(/\d+/g) || []).join('');
  }
};

const buildSanitizedForm = (form, isValid) => {
  if (!form) {
    return {};
  }
  const basicSanitization = {
    ...form,
    [FORM_REQUIREMENTS.IS_VALID_UNIFIED_PROMO_CODE]: isValid,
    [FORM_REQUIREMENTS.BILLING_ADDRESS]: buildSanitizedAddress(form[FORM_REQUIREMENTS.BILLING_ADDRESS]),
    [FORM_REQUIREMENTS.PICKUP_ADDRESS]: buildSanitizedAddress(form[FORM_REQUIREMENTS.PICKUP_ADDRESS]),
    [FORM_REQUIREMENTS.PAYMENT_METHOD]: buildSanitizedPaymentMethod(form[FORM_REQUIREMENTS.PAYMENT_METHOD]),
  };
  return deepTrim(basicSanitization, [STAMPS_CREDENTIALS_FIELDS.PASSWORD]);
};

const isCouponUSPSPromoCode = (coupon) => {
  const regex = /^USPS/;
  return regex.test(coupon);
};

const isValidPromoCode = async (coupon) => {
  const isCouponUSPS = isCouponUSPSPromoCode(coupon);
  if (isCouponUSPS) {
    try {
      return await postPromoCodeValidation(coupon);
    } catch {
      // do nothing. The default value will be used and this will not block registration.
    }
  }
  return false;
};

export const useSubmitRegistration = () => {
  const [loading, setLoading] = useAsyncSafeState(false);
  const coupon = useSelector(Global.selectSellerCouponCode);
  const saveAddressToSellerCache = useSaveFirstTimeBillingInfo();

  const submitRegistration = useCallback(
    async (form, isUpdate) => {
      const phoneWithOnlyNumbers = clearNonNumericalCharsFromPhone(form);
      const formToSanitize =
        isUpdate || !phoneWithOnlyNumbers
          ? form
          : {
              ...form,
              BillingAddress: {
                ...form.BillingAddress,
                phone: phoneWithOnlyNumbers,
              },
            };

      setLoading(true);
      try {
        const sanitizedForm = buildSanitizedForm(formToSanitize, await isValidPromoCode(coupon));
        const results = await registerWalletPaymentMethod(sanitizedForm, isUpdate);
        saveAddressToSellerCache({
          [AddressTypeLookup.walletBilling]: sanitizedForm[FORM_REQUIREMENTS.BILLING_ADDRESS],
          [AddressTypeLookup.walletPickup]: sanitizedForm[FORM_REQUIREMENTS.PICKUP_ADDRESS],
        });

        // handle v2/payments contract error response
        if (!results.success) {
          return {
            errors: [
              {
                Message: results.errorMessage || 'Something went wrong trying to Register.',
              },
            ],
          };
        }

        return { results: results.walletResult };
      } catch (errorResponse) {
        return { errors: errorResponse?.response?.data?.Errors || [] };
      } finally {
        setLoading(false);
      }
    },
    [coupon, saveAddressToSellerCache, setLoading],
  );

  return {
    loading,
    submitRegistration,
  };
};
